import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Deployment from "../../services/deployment.js";
import { useHasPermission, useHasAnyPermission, useDeploymentStages, useUnlockedDeployments, useDeploymentEditPermissions } from './Hooks.jsx';
import { useAppContext } from "../ApplicationServicesContext";
import PubSub from "../../services/PubSub.js";
import UI_PERMISSIONS_NAME from "../../config/uiPermissionsNames.js";
import ROUTES from "../routes";
import ConfirmationPrompt from "../../components/controls/ConfirmationPrompt.jsx";
import { Button } from "react-bootstrap";
import Toast from "../../components/controls/Toast.jsx";

const useNavbar = (() => {
    const { id } = useParams();
    const { setDeploymentStage } = useDeploymentStages();
    const { unlockDeployment, lockDeployment, isUnlocked, unlockedTimeRemaining } = useUnlockedDeployments();

    const viewUsersPermission = useHasAnyPermission({
        names: [UI_PERMISSIONS_NAME.ViewUsers, UI_PERMISSIONS_NAME.EditUsers]
    });
    const viewBuildtimePermission = useHasAnyPermission({
        deployment: id,
        names: [UI_PERMISSIONS_NAME.ViewBuildtime, UI_PERMISSIONS_NAME.EditBuildtime]
    });
    const viewRuntimePermission = useHasAnyPermission({
        deployment: id,
        names: [UI_PERMISSIONS_NAME.ViewRuntime, UI_PERMISSIONS_NAME.EditRuntime]
    });
    const viewEnvironmentsPermission = useHasAnyPermission({
        names: [UI_PERMISSIONS_NAME.ViewEnvironments, UI_PERMISSIONS_NAME.EditEnvironments]
    });
    const viewDomainPermission = useHasAnyPermission({
        names: [UI_PERMISSIONS_NAME.ViewDomain, UI_PERMISSIONS_NAME.EditDomain]
    });
    const viewRootDomainPermission = useHasAnyPermission({
        names: [UI_PERMISSIONS_NAME.ViewRootDomain, UI_PERMISSIONS_NAME.EditDomain]
    });
    const viewSessionLogsPermission = useHasPermission({
        deployment: id,
        name: UI_PERMISSIONS_NAME.ViewSyncLogs
    });
    const viewNotificationsPermission = useHasPermission({
        deployment: id,
        name: UI_PERMISSIONS_NAME.Notifications
    });
    const viewDeploymentPermission = useHasAnyPermission({
        deployment: id,
        names: [UI_PERMISSIONS_NAME.ViewDeployment, UI_PERMISSIONS_NAME.EditDeployment]
    });

    const [translate] = useTranslation();
    const navigate = useNavigate();

    const { isRootDomain, domain, user } = useAppContext();
    const [drillDownObjects, setDrillDownObjects] = useState([]);
    const [deployments, setDeployments] = useState([]);
    const [cursors, setCursors] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [deploymentParams, setDeploymentParams] = useState({
        pageIncrement: 0
    });
    const [selectedTab, setSelectedTab] = useState("domain-management");
    const [onPremDeployment, setOnPremDeployment] = useState(false);
    const [showConfirmUnlock, setShowConfirmUnlock] = useState(false);
    const [hasRuntimeLanguages, setHasRuntimeLanguages] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const shouldAutoSelectTab = useRef(false);
    const deploymentEditPermission = useDeploymentEditPermissions(deployments);

    const domainManagementTabs = useCallback(() => [
        {
            name: translate("OTM_USERS"),
            id: ROUTES.USERS,
            visible: viewUsersPermission
        },
        {
            name: translate("OTM_ENVIRONMENTS"),
            id: ROUTES.ENVIRONMENTS,
            visible: viewEnvironmentsPermission
        },
        {
            name: translate("OTM_DOMAIN_CONFIGURATION"),
            id: ROUTES.DOMAIN_CONFIGURATION,
            visible: viewDomainPermission,
            nestedViews: [
                {
                    name: translate("OTM_SETTINGS"),
                    id: "settings",
                    urlPath: `${domain._id}/settings`,
                    visible: viewDomainPermission,
                },
                {
                    name: translate("OTM_AI_PROVIDERS"),
                    id: "ai-providers",
                    urlPath: `${domain._id}/ai-providers`,
                    visible: viewDomainPermission,
                },
                {
                    name: translate("OTM_PRODUCTS"),
                    id: "products",
                    urlPath: `${domain._id}/products`,
                    visible: viewDomainPermission,
                },
            ]
        },
        {
            name: translate("OTM_EMAIL_MANAGEMENT"),
            id: ROUTES.EMAIL_MANAGEMENT,
            visible: user?.appAdmin || user?.adminForDomains?.includes(domain._id),
        },
        {
            name: translate("OTM_SETTINGS_COMPARISON"),
            id: ROUTES.SETTINGS_COMPARISON,
            visible: viewDomainPermission
        },
        {
            name: translate("OTM_AUDIT_TRAIL"),
            id: ROUTES.AUDIT_TRAIL,
            visible: user?.appAdmin || user?.adminForDomains?.includes(domain._id)
        },
        {
            name: translate("OTM_DOWNLOADABLES"),
            id: ROUTES.DOWNLOADABLES,
            visible: user?.appAdmin || user?.adminForDomains?.includes(domain._id)
        },
        {
            name: translate("OTM_SITE_MONITORING"),
            id: ROUTES.SITE_MONITORING,
            visible: viewDomainPermission
        }
    ], [domain, translate, viewDomainPermission, viewEnvironmentsPermission, viewUsersPermission, user?.appAdmin, user?.adminForDomains]);

    const populateDrillDownObjects = useCallback(() => {
        setDrillDownObjects(() => {
            if (isRootDomain) {
                return [
                    {
                        name: translate("OTM_USERS"),
                        id: ROUTES.USERS,
                        visible: user.appAdmin
                    },
                    {
                        name: translate("OTM_PRODUCTS"),
                        id: ROUTES.PRODUCTS,
                        visible: viewRootDomainPermission
                    },
                    {
                        name: translate("OTM_DOMAINS"),
                        id: ROUTES.DOMAINS,
                        visible: viewRootDomainPermission
                    },
                    {
                        name: translate("OTM_SETTINGS_COMPARISON"),
                        id: ROUTES.SETTINGS_COMPARISON,
                        visible: viewRootDomainPermission
                    },
                    {
                        name: translate("OTM_AI_PIPELINES"),
                        id: ROUTES.AI_PIPELINES,
                        visible: viewRootDomainPermission
                    },
                    {
                        name: translate("OTM_ENVIRONMENT_TEMPLATES"),
                        id: ROUTES.ENVIRONMENT_TEMPLATES,
                        visible: viewRootDomainPermission
                    },
                    {
                        name: translate("OTM_AUDIT_TRAIL"),
                        id: ROUTES.AUDIT_TRAIL,
                        visible: viewRootDomainPermission
                    },
                    {
                        name: translate("OTM_LOGS"),
                        id: ROUTES.DOMAIN_LOGS,
                        visible: true,
                    }
                ]
            }

            if (selectedTab === "domain-management") {
                return domainManagementTabs();
            }

            return [
                {
                    name: translate("OTM_DEPLOYMENT_MANAGEMENT"),
                    id: ROUTES.DEPLOYMENT_MANAGEMENT,
                    visible: viewDeploymentPermission
                },
                {
                    name: translate("OTM_RUNTIME_CONFIG"),
                    id: ROUTES.RUNTIME_CONFIG,
                    visible: onPremDeployment === false && viewRuntimePermission,
                    nestedViews: [
                        {
                            name: translate("OTM_SETTINGS"),
                            id: "settings",
                            urlPath: "settings",
                            visible: onPremDeployment === false && viewRuntimePermission,
                        },
                        {
                            name: translate("OTM_LANGUAGES"),
                            id: "languages",
                            urlPath: "languages",
                            visible: onPremDeployment === false && hasRuntimeLanguages,
                        },
                    ]
                },
                {
                    name: translate("OTM_BUILD_TIME_CONFIG"),
                    id: ROUTES.BUILD_TIME_CONFIG,
                    visible: viewBuildtimePermission,
                    nestedViews: [
                        {
                            name: translate("OTM_DATA"),
                            id: "data",
                            urlPath: "data",
                            visible: viewBuildtimePermission,
                        },
                        {
                            name: translate("OTM_SYSTEM"),
                            id: "system",
                            urlPath: "system",
                            visible: viewBuildtimePermission,
                        },
                        {
                            name: translate("OTM_UI"),
                            id: "ui",
                            urlPath: "ui",
                            visible: viewBuildtimePermission,
                        },
                    ]
                },
                {
                    name: translate("OTM_NOTIFICATIONS"),
                    id: ROUTES.NOTIFICATIONS,
                    visible: viewNotificationsPermission,
                    nestedViews: [
                        {
                            name: translate("OTM_NOTIFICATIONS_TEMPLATES"),
                            id: "templates",
                            urlPath: "templates",
                            visible: viewNotificationsPermission,
                        },
                        {
                            name: translate("OTM_NOTIFICATIONS_USERS"),
                            id: "users",
                            urlPath: "users",
                            visible: viewNotificationsPermission,
                        },
                        {
                            name: translate("OTM_NOTIFICATIONS_HISTORY"),
                            id: "history",
                            urlPath: "history",
                            visible: viewNotificationsPermission,
                        },
                    ]
                },
                {
                    name: translate("OTM_SUPPORT_LOGS"),
                    id: ROUTES.SUPPORT_LOGS,
                    visible: viewSessionLogsPermission
                },
            ];
        });
    }, [user.appAdmin, domainManagementTabs, onPremDeployment, viewSessionLogsPermission, viewRuntimePermission, hasRuntimeLanguages,
        setDrillDownObjects, translate, selectedTab, isRootDomain, viewNotificationsPermission, viewBuildtimePermission, viewDeploymentPermission,
        viewRootDomainPermission]);

    const handleDeploymentClick = useCallback((deployment) => {
        navigate(`/${ROUTES.DEPLOYMENT_MANAGEMENT}/${deployment._id}`);
        shouldAutoSelectTab.current = deployment._id;
    }, [navigate]);

    const getAllDeployments = useCallback(async (params) => {
        setIsLoading(true);
        setDeployments([]);
        const response = await Deployment.getAllDeploymentsInDomain(
            params.domain,
            null,
            params.firstCursor,
            params.lastCursor,
            params.pageIncrement < 0,
            params.getLatest
        );

        if (response && !response.error) {
            setDeployments(response.data);
            setCurrentPage(page => {
                const result = params.getLatest ? Math.ceil(response.paging.count / response.paging.pageSize) : page + params.pageIncrement;
                setHasNextPage(response.paging.count > result * response.paging.pageSize);
                return result;
            });
            setCursors({
                firstCursor: response.paging.firstCursor,
                lastCursor: response.paging.lastCursor
            });

            if (response.data.length > 0 && window.location.pathname === '/') {
                handleDeploymentClick(response.data[0]);
            }
        }

        setDeploymentParams({});
        setIsLoading(false);
    }, [setDeployments, handleDeploymentClick]);

    const getDeployment = useCallback(async (deploymentId) => {
        const response = await Deployment.getDeployment(deploymentId);

        if (response && !response.error) {
            setOnPremDeployment(response.data.productVersion?.onPrem);
            setHasRuntimeLanguages(response.data.productVersion?.runtimeLanguages);
        }
    }, []);

    useEffect(() => {
        const id = PubSub.subscribe("refresh-navbar-deployment", async ({ deploymentId }) => {
            getDeployment(deploymentId);
        });

        return () => PubSub.unsubscribe("refresh-navbar-deployment", id);
    }, [getDeployment]);

    useEffect(() => {
        if (deployments?.length > 0) {
            deployments.forEach(deployment => {
                setDeploymentStage(deployment._id, deployment.environment?.stage);
            })
        }
    }, [deployments, setDeploymentStage])

    useEffect(() => {
        if (shouldAutoSelectTab.current) {
            const visibleTabs = drillDownObjects.filter(obj => obj.visible === true);
            if (visibleTabs && visibleTabs.length > 0) {
                navigate(`/${visibleTabs[0].id}/${shouldAutoSelectTab.current}`);
                shouldAutoSelectTab.current = false;
            }
        }
    }, [drillDownObjects, navigate]);

    useEffect(() => {
        populateDrillDownObjects();
    }, [populateDrillDownObjects]);

    useEffect(() => {
        if (domain && deploymentParams.pageIncrement !== undefined) {
            getAllDeployments({ ...deploymentParams, domain: domain._id });
        }

        const id = PubSub.subscribe("refresh-environment-deployments", () => {
            setCursors({});
            setCurrentPage(1);
            setHasNextPage(false);
            setDeploymentParams({ pageIncrement: 0 });
        });

        return () => {
            PubSub.unsubscribe("refresh-environment-deployments", id);
        };
    }, [getAllDeployments, domain, deploymentParams]);

    useEffect(() => {
        if (deployments && deployments.length > 0) {
            deployments.forEach((deployment) => {
                if (deployment._id === id) {
                    setSelectedTab(deployment);
                    getDeployment(id);
                }
            })
        }
    }, [deployments, setSelectedTab, getDeployment, id]);

    useEffect(() => {
        const subId = PubSub.subscribe("set-selected-tab", (payload) => {
            if (!payload || typeof payload !== "string") {
                return;
            }

            if (!drillDownObjects.map(o => o.id).includes(payload)) {
                return;
            }

            setSelectedTab(payload);
        })

        return () => PubSub.unsubscribe("set-selected-tab", subId)
    })

    const previous = useCallback(() => {
        setDeploymentParams({
            pageIncrement: -1,
            firstCursor: cursors.firstCursor,
            lastCursor: cursors.lastCursor
        });
    }, [cursors.firstCursor, cursors.lastCursor]);

    const next = useCallback(() => {
        setDeploymentParams({
            pageIncrement: 1,
            firstCursor: cursors.firstCursor,
            lastCursor: cursors.lastCursor
        });
    }, [cursors.firstCursor, cursors.lastCursor]);

    const handleDrillDownClick = useCallback((url, deploymentId, nestedViews) => {
        navigate(`/${url}${deploymentId ? '/' + deploymentId : ''}${nestedViews ? '/' + nestedViews[0].urlPath : ''}`);
    }, [navigate]);

    const handleNestedDrillDownClick = useCallback((url, deploymentId, nestedItem) => {
        navigate(`/${url}${deploymentId ? '/' + deploymentId : ''}/${nestedItem}`);
    }, [navigate]);

    const navigateToLandingPage = useCallback(() => {
        for (var i = 0; i < domainManagementTabs().length; i++) {
            if (domainManagementTabs()[i].visible) {
                handleDrillDownClick(domainManagementTabs()[i].id);
                break;
            }
        }
    }, [domainManagementTabs, handleDrillDownClick]);

    const handleDomainManagementClick = useCallback(() => {
        setSelectedTab("domain-management");
        navigateToLandingPage();
    }, [navigateToLandingPage]);

    const handleUnlockClick = useCallback((deployment) => {
        setShowConfirmUnlock(deployment);
    }, []);

    const confirmationPrompt = useMemo(() => {
        if (!showConfirmUnlock) return null;

        return <>
            <ConfirmationPrompt
                show={showConfirmUnlock}
                message={!isUnlocked(showConfirmUnlock._id)
                    ? translate("OTM_CONFIRM_UNLOCK", { deployment: showConfirmUnlock.name, environment: showConfirmUnlock.environment?.stage })
                    : translate("OTM_UNLOCK_TIME_REMAINING", { deployment: showConfirmUnlock.name, environment: showConfirmUnlock.environment?.stage, time: unlockedTimeRemaining(showConfirmUnlock._id) })
                }
                manualInput={isUnlocked(showConfirmUnlock._id) ? false : true}
                manualInputText={showConfirmUnlock.name}
                showConfirm={isUnlocked(showConfirmUnlock._id) ? false : true}
                onConfirm={() => { setShowConfirmUnlock(false); unlockDeployment(showConfirmUnlock._id); }}
                onCancel={() => setShowConfirmUnlock(false)}
                renderButtons={() => {
                    if (!isUnlocked(showConfirmUnlock._id)) return null;
                    return <Button
                        onClick={() => { setShowConfirmUnlock(false); lockDeployment(showConfirmUnlock._id) }}
                        variant="danger"
                        className="btn-sm"
                    >
                        {translate("OTM_LOCK")}
                    </Button>
                }}
            />
        </>
    }, [showConfirmUnlock, isUnlocked, translate, unlockedTimeRemaining, unlockDeployment, lockDeployment]);

    const unlockTimerToast = useMemo(() => {
        return <Toast
            showId="deployment-unlock-timer-show"
            hideId="deployment-unlock-timer-hide"
            delay={120000}
        />
    }, [])

    return {
        isLoading,
        drillDownObjects,
        deployments,
        selectedTab,
        next,
        previous,
        currentPage,
        hasNextPage,
        handleDomainManagementClick,
        handleDeploymentClick,
        handleDrillDownClick,
        handleNestedDrillDownClick,
        navigateToLandingPage,
        isUnlocked,
        handleUnlockClick,
        confirmationPrompt,
        unlockTimerToast,
        deploymentEditPermission,
    }
});

export default useNavbar;